<script setup lang="ts">
/** DR-14 · Search pages and already-listed entities by name, then open the chosen destination. */
import Dialog from '~/components/ui/Dialog.vue'
import Icon from '~/components/ui/Icon.vue'
import StatusCell from '~/components/ui/StatusCell.vue'
import type { IconName } from '~/utils/icons'

export interface SearchHit { id: string; kind: string; icon: IconName; title: string; fact?: string; status?: string; to: string }
const props = withDefaults(defineProps<{ hits: SearchHit[]; recent: SearchHit[]; query: string; pending?: boolean }>(), { pending: false })
const emit = defineEmits<{ close: []; open: [hit: SearchHit]; 'update:query': [value: string] }>()
const input = ref<HTMLInputElement | null>(null)
const active = ref(0)
const list = computed(() => props.query.trim() ? props.hits : props.recent)
const groups = computed(() => {
  const order: string[] = []
  const grouped = new Map<string, SearchHit[]>()
  for (const hit of list.value) {
    if (!grouped.has(hit.kind)) {
      grouped.set(hit.kind, [])
      order.push(hit.kind)
    }
    grouped.get(hit.kind)?.push(hit)
  }
  return order.map((kind) => ({ kind, hits: grouped.get(kind) ?? [] }))
})
watch(list, () => { active.value = 0 })
function onKey(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    active.value = Math.min(list.value.length - 1, active.value + 1)
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    active.value = Math.max(0, active.value - 1)
  } else if (event.key === 'Enter') {
    const hit = list.value[active.value]
    if (hit) emit('open', hit)
  }
}
const index = (hit: SearchHit) => list.value.indexOf(hit)
const onInput = (event: Event) => emit('update:query', (event.target as HTMLInputElement).value)
onMounted(() => input.value?.focus())
</script>

<template>
  <Dialog title="Search or jump to" :meta="query.trim() ? (pending ? 'reading…' : list.length + ' matches') : 'recent'" :width="600" @close="emit('close')">
    <template #icon><Icon name="search" /></template>
    <label class="flex h-[var(--control-height)] items-center gap-2 rounded-[var(--radius-control)] border border-border bg-muted/40 px-2.5 focus-within:border-primary/60">
      <Icon name="search" :size="13" class="shrink-0 text-muted-foreground" />
      <input ref="input" :value="query" type="search" placeholder="Search pages, documents, models, endpoints…" class="min-w-0 flex-1 bg-transparent text-[length:var(--type-control)] outline-none placeholder:text-muted-foreground/60" @input="onInput" @keydown="onKey">
      <kbd class="num rounded-[3px] border border-border px-1 py-0.5 text-[length:var(--type-badge)] text-muted-foreground">esc</kbd>
    </label>
    <div class="-mx-3.5 -mb-3.5 max-h-[var(--measure-400px)] overflow-auto border-t border-border">
      <p v-if="query.trim() && !pending && !list.length" class="m-0 px-3.5 py-6 text-center text-xs text-muted-foreground">Nothing matches “{{ query }}”</p>
      <template v-for="group in groups" :key="group.kind">
        <div class="flex h-7 items-center px-3.5 text-[length:var(--type-meta)] font-semibold text-muted-foreground">{{ group.kind }}</div>
        <button
v-for="hit in group.hits" :key="hit.id" type="button"
                class="grid h-[var(--row-height)] w-full items-center gap-x-3 px-3.5 text-left"
                :class="index(hit) === active ? 'bg-sidebar-accent' : 'hover:bg-sidebar-accent/60'"
                style="grid-template-columns: var(--measure-14px) minmax(0,1fr) auto"
                @mouseenter="active = index(hit)" @click="emit('open', hit)">
          <Icon :name="hit.icon" :size="13" class="text-muted-foreground" />
          <span class="truncate text-xs">{{ hit.title }}</span>
          <span class="flex items-center gap-2 text-[length:var(--type-meta)]">
            <StatusCell v-if="hit.status" :status="hit.status" />
            <span v-if="hit.fact" class="num text-muted-foreground">{{ hit.fact }}</span>
            <kbd v-if="index(hit) === active" class="num rounded-[3px] border border-border px-1 py-0.5 text-[length:var(--type-badge)] text-muted-foreground">↵</kbd>
          </span>
        </button>
      </template>
    </div>
  </Dialog>
</template>
